// src/pages/UnitsPage.jsx - 优化版本（限制高度，内部滚动）
import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { unitApi } from '../api';
import UnitDialog from '../components/admin/UnitDialog';

const UnitsPage = () => {
  const { language } = useApp();
  const t = translations[language];
  
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  useEffect(() => {
    loadUnits();
    loadStats();
  }, []);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const res = await unitApi.getList({ status: 'active' });
      if (res.success) {
        setUnits(res.data || []);
      }
    } catch (err) {
      console.error('加载单位失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await unitApi.getStats();
      if (res.success) {
        setStats(res.data || []);
      }
    } catch (err) {
      console.error('加载统计失败:', err);
    }
  };

  const handleAdd = () => {
    setEditingUnit(null);
    setDialogOpen(true);
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setDialogOpen(true);
  };

  const handleDelete = async (unit) => {
    const stat = stats.find(s => s.id === unit.id);
    if (stat && stat.product_count > 0) {
      alert(
        language === 'en' ? `Cannot delete. ${stat.product_count} products are using this unit.` :
        language === 'km' ? `មិនអាចលុបបានទេ។ មានផលិតផល ${stat.product_count} កំពុងប្រើប្រាស់។` :
        `无法删除,有 ${stat.product_count} 个商品正在使用此单位`
      );
      return;
    }

    if (!window.confirm(
      language === 'en' ? 'Are you sure you want to delete this unit?' :
      language === 'km' ? 'តើអ្នកប្រាកដថាចង់លុបឯកតានេះទេ?' :
      '确定要删除此单位吗?'
    )) {
      return;
    }

    try {
      const res = await unitApi.delete(unit.id);
      if (res.success) {
        alert(
          language === 'en' ? 'Unit deleted successfully' :
          language === 'km' ? 'លុបឯកតាបានជោគជ័យ' :
          '删除成功'
        );
        loadUnits();
        loadStats();
      }
    } catch (err) {
      console.error('删除失败:', err);
      alert(
        language === 'en' ? 'Failed to delete unit' :
        language === 'km' ? 'បរាជ័យក្នុងការលុប' :
        '删除失败'
      );
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editingUnit) {
        const res = await unitApi.update(editingUnit.id, data);
        if (res.success) {
          alert(
            language === 'en' ? 'Unit updated successfully' :
            language === 'km' ? 'ធ្វើបច្ចុប្បន្នភាពបានជោគជ័យ' :
            '更新成功'
          );
        }
      } else {
        const res = await unitApi.create(data);
        if (res.success) {
          alert(
            language === 'en' ? 'Unit created successfully' :
            language === 'km' ? 'បង្កើតបានជោគជ័យ' :
            '创建成功'
          );
        }
      }
      setDialogOpen(false);
      loadUnits();
      loadStats();
    } catch (err) {
      console.error('提交失败:', err);
      alert(
        language === 'en' ? 'Operation failed' :
        language === 'km' ? 'បរាជ័យ' :
        '操作失败'
      );
    }
  };

  const getUnitName = (unit) => {
    switch (language) {
      case 'en':
        return unit.name_en || unit.name;
      case 'km':
        return unit.name_km || unit.name;
      default:
        return unit.name;
    }
  };

  const getCategoryName = (category) => {
    const categoryMap = {
      piece: { zh: '件数', en: 'Piece', km: 'ចំនួន' },
      weight: { zh: '重量', en: 'Weight', km: 'ទម្ងន់' },
      volume: { zh: '体积', en: 'Volume', km: 'ទំហំ' },
      length: { zh: '长度', en: 'Length', km: 'បណ្តោយ' },
      other: { zh: '其他', en: 'Other', km: 'ផ្សេងទៀត' }
    };
    
    return categoryMap[category]?.[language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh'] || category;
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 - 固定 */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">
            {language === 'en' ? 'Unit Management' :
             language === 'km' ? 'គ្រប់គ្រងឯកតា' :
             '单位管理'}
          </h2>
          <p className="text-gray-600 mt-1">
            {language === 'en' ? 'Manage product measurement units' :
             language === 'km' ? 'គ្រប់គ្រងឯកតារង្វាស់ផលិតផល' :
             '管理商品计量单位'}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>
            {language === 'en' ? 'Add Unit' :
             language === 'km' ? 'បន្ថែមឯកតា' :
             '添加单位'}
          </span>
        </button>
      </div>

      {/* 表格区域 - 可滚动 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">
                {language === 'en' ? 'Loading...' :
                 language === 'km' ? 'កំពុងផ្ទុក...' :
                 '加载中...'}
              </p>
            </div>
          </div>
        ) : units.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>
                {language === 'en' ? 'No units found' :
                 language === 'km' ? 'រកមិនឃើញឯកតា' :
                 '暂无单位'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language === 'en' ? 'Name' :
                     language === 'km' ? 'ឈ្មោះ' :
                     '名称'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language === 'en' ? 'Category' :
                     language === 'km' ? 'ប្រភេទ' :
                     '类别'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language === 'en' ? 'Products' :
                     language === 'km' ? 'ផលិតផល' :
                     '商品数'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {language === 'en' ? 'Actions' :
                     language === 'km' ? 'សកម្មភាព' :
                     '操作'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {units.map((unit) => {
                  const stat = stats.find(s => s.id === unit.id);
                  return (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {unit.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getUnitName(unit)}
                        </div>
                        {language !== 'zh' && unit.name && (
                          <div className="text-xs text-gray-500">{unit.name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {getCategoryName(unit.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Package className="w-4 h-4 mr-1 text-gray-400" />
                          {stat?.product_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(unit)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={t.edit}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(unit)}
                            className={`transition-colors ${
                              stat?.product_count > 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-800'
                            }`}
                            disabled={stat?.product_count > 0}
                            title={t.delete}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 对话框 */}
      {dialogOpen && (
        <UnitDialog
          unit={editingUnit}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default UnitsPage;